import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, LoadingBlock } from "@/components/Common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

function Anchor({ id, children }) {
    return (
        <div id={id} className="scroll-mt-24">
            {children}
        </div>
    );
}

export default function Strategy() {
    const [doc, setDoc] = useState(null);

    useEffect(() => {
        api.get("/strategy/document").then((r) => setDoc(r.data));
    }, []);

    const toc = useMemo(() => (doc?.sections || []).map((s) => ({ id: s.id, title: s.title })), [doc]);
    if (!doc) return <LoadingBlock rows={8} />;

    return (
        <>
            <PageHeader
                eyebrow="Architecture & Strategy"
                title={doc.title}
                subtitle={doc.subtitle}
            />
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <aside className="lg:col-span-3 lg:sticky lg:top-6 self-start" data-testid="strategy-toc">
                    <Card>
                        <CardHeader className="pb-2"><CardTitle className="text-sm">Contents</CardTitle></CardHeader>
                        <CardContent>
                            <ul className="space-y-1.5 text-sm">
                                {toc.map((s) => (
                                    <li key={s.id}>
                                        <a href={`#${s.id}`} className="text-muted-foreground hover:text-foreground transition-colors">
                                            {s.title}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </CardContent>
                    </Card>
                </aside>

                <main className="lg:col-span-9 space-y-4" data-testid="strategy-document">
                    {doc.sections.map((sec) => (
                        <Anchor key={sec.id} id={sec.id}>
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg tracking-tight">{sec.title}</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3 text-sm leading-relaxed">
                                    {sec.body && <p>{sec.body}</p>}

                                    {sec.modules && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            {sec.modules.map((m) => (
                                                <div key={m.name} className="rounded-xl border border-border p-3 bg-secondary/30">
                                                    <div className="font-semibold">{m.name}</div>
                                                    <div className="text-xs text-muted-foreground mb-2">{m.purpose}</div>
                                                    <dl className="text-xs space-y-0.5">
                                                        <div><span className="text-muted-foreground">Language:</span> {m.language}</div>
                                                        <div><span className="text-muted-foreground">Self-hosted:</span> {m.self_hosted}</div>
                                                        <div><span className="text-muted-foreground">Datastore:</span> {m.datastore}</div>
                                                        <div><span className="text-muted-foreground">API:</span> {m.api}</div>
                                                        <div><span className="text-muted-foreground">Integrations:</span> {m.integrations}</div>
                                                        <div><span className="text-muted-foreground">Migration:</span> {m.migration}</div>
                                                        <div><span className="text-muted-foreground">Security:</span> {m.security}</div>
                                                        <div><span className="text-muted-foreground">Failure:</span> {m.failure}</div>
                                                        <div><span className="text-muted-foreground">Scalability:</span> {m.scalability}</div>
                                                    </dl>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {sec.items && Array.isArray(sec.items) && sec.items.length > 0 && typeof sec.items[0] === "object" && "category" in sec.items[0] && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {sec.items.map((it, i) => (
                                                <div key={i} className="flex items-baseline gap-2 text-sm">
                                                    <span className="text-xs uppercase tracking-wider text-muted-foreground min-w-[120px]">{it.category}</span>
                                                    <span>{it.choice}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {sec.items && Array.isArray(sec.items) && sec.items.length > 0 && typeof sec.items[0] === "object" && "risk" in sec.items[0] && (
                                        <div className="space-y-2">
                                            {sec.items.map((it, i) => (
                                                <div key={i} className="rounded-lg border border-border p-3">
                                                    <div className="text-sm font-medium">{it.risk}</div>
                                                    <div className="text-xs text-muted-foreground mt-1">Mitigation: {it.mitigation}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {sec.items && Array.isArray(sec.items) && typeof sec.items[0] === "string" && (
                                        <ul className="list-disc pl-5 space-y-1">
                                            {sec.items.map((it, i) => (
                                                <li key={i}>{it}</li>
                                            ))}
                                        </ul>
                                    )}

                                    {sec.steps && (
                                        <ol className="list-decimal pl-5 space-y-1">
                                            {sec.steps.map((st, i) => (
                                                <li key={i}>{st}</li>
                                            ))}
                                        </ol>
                                    )}

                                    {sec.stages && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="strategy-loop">
                                            {sec.stages.map((s, i) => (
                                                <div key={i} className="rounded-lg border border-border p-3">
                                                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Stage {i + 1}</div>
                                                    <div className="font-medium">{s.stage}</div>
                                                    <div className="text-sm mt-1">{s.detail}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {sec.table && (
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm" data-testid="strategy-language-table">
                                                <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                                                    <tr>
                                                        <th className="text-left py-2 pr-3">Language</th>
                                                        <th className="text-left py-2 pr-3">Used for</th>
                                                        <th className="text-left py-2 pr-3">Why</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {sec.table.map((row, i) => (
                                                        <tr key={i} className="border-t border-border">
                                                            <td className="py-2 pr-3 font-medium">{row.language}</td>
                                                            <td className="py-2 pr-3">{row.used_for}</td>
                                                            <td className="py-2 pr-3 text-muted-foreground">{row.why}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    {sec.diagram && (
                                        <pre className="text-[11px] sm:text-xs bg-secondary rounded-lg p-3 overflow-x-auto whitespace-pre font-mono" data-testid="strategy-diagram">
{sec.diagram}
                                        </pre>
                                    )}

                                    {sec.entities && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {sec.entities.map((e, i) => (
                                                <div key={i} className="rounded-lg border border-border p-3">
                                                    <div className="font-medium">{e.name}</div>
                                                    <div className="text-xs text-muted-foreground">{e.keys}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {sec.timeline && (
                                        <div className="space-y-3" data-testid="strategy-roadmap">
                                            {sec.timeline.map((q, i) => (
                                                <div key={i} className="flex gap-3">
                                                    <div className="w-12 shrink-0">
                                                        <div className="text-sm font-semibold tabular">{q.quarter}</div>
                                                    </div>
                                                    <div className="flex-1">
                                                        <ul className="text-sm list-disc pl-4 space-y-0.5">
                                                            {q.milestones.map((m, j) => <li key={j}>{m}</li>)}
                                                        </ul>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </Anchor>
                    ))}
                </main>
            </div>
        </>
    );
}
